import React from 'react';
import './celebrations.css';

/**
 * kind → animation, the dumb-client half of the backend celebration catalog
 * (aswb-backend src/config/celebrationCatalog.ts). The backend decides WHICH
 * moment fires and its copy; this maps the kind to the approved motion
 * (claude.ai/code/artifact/e44103b0-7e0a-4702-912f-6fcdba64e7ef). Unknown
 * kinds render null — callers keep their existing confetti as the fallback.
 */

const TEAL = '#0F766E';
const PLUM = '#6B21A8';
const GOLD = '#D97706';
const INK = '#2A2724';

const FirstCorrect: React.FC = () => (
  <div className="relative inline-block" data-testid="cm-first_correct">
    <div className="cm-stamp flex items-center justify-center" style={{ width: 92, height: 92, borderRadius: 22, background: TEAL }}>
      <svg width="52" height="52" viewBox="0 0 52 52"><path d="M10 28 L22 40 L43 13" fill="none" stroke="#fff" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </div>
    <span className="cm-spark" style={{ ['--dx' as string]: '-44px', ['--dy' as string]: '-38px', left: 40, top: 30, background: GOLD }} />
    <span className="cm-spark" style={{ ['--dx' as string]: '50px', ['--dy' as string]: '-30px', left: 44, top: 26, background: GOLD, animationDelay: '.08s' }} />
    <span className="cm-spark" style={{ ['--dx' as string]: '34px', ['--dy' as string]: '44px', left: 48, top: 44, background: PLUM, animationDelay: '.12s' }} />
  </div>
);

const DiagnosticDone: React.FC<{ bandAngle?: number }> = ({ bandAngle = 38 }) => (
  <div data-testid="cm-diagnostic_done">
    <svg width="200" height="120" viewBox="0 0 200 120">
      <path d="M20 96 A80 80 0 0 1 180 96" fill="none" stroke="#D8CFC0" strokeWidth="16" strokeLinecap="round" />
      <path d="M20 96 A80 80 0 0 1 100 16" fill="none" stroke={TEAL} strokeWidth="16" strokeLinecap="round" />
      <path d="M100 16 A80 80 0 0 1 152 34" fill="none" stroke={GOLD} strokeWidth="16" strokeLinecap="round" />
      <g className="cm-needle" style={{ ['--band-angle' as string]: `${bandAngle}deg` }}>
        <rect x="96" y="34" width="8" height="62" rx="4" fill={INK} />
        <circle cx="100" cy="96" r="10" fill={INK} />
      </g>
    </svg>
  </div>
);

const SignupWelcome: React.FC = () => (
  <div className="cm-stamp cm-ledge inline-block px-6 py-4 text-center" data-testid="cm-signup_welcome">
    <div className="font-display font-bold text-2xl" style={{ color: TEAL }}>SWTPA</div>
    <div className="text-xs font-extrabold" style={{ color: '#6B6257' }}>seat reserved</div>
  </div>
);

const FirstQuiz: React.FC = () => (
  <div className="relative inline-flex items-center justify-center" data-testid="cm-first_quiz">
    {['A', 'B', 'C'].map((letter, i) => (
      <div key={letter} className="cm-pop cm-ledge flex items-center justify-center font-display font-bold text-xl"
        style={{ width: 64, height: 84, background: '#F7F1E6', color: INK, transform: `rotate(${[-14, -4, 9][i]}deg)`,
          marginRight: i === 0 ? -28 : 0, marginLeft: i === 2 ? -28 : 0, zIndex: i === 1 ? 1 : 0, animationDelay: `${0.05 + i * 0.1}s` }}>
        {letter}
      </div>
    ))}
    <div className="cm-medal absolute flex items-center justify-center font-display font-bold text-3xl text-white"
      style={{ zIndex: 2, width: 76, height: 76, borderRadius: '50%', background: GOLD, border: `3px solid ${INK}`, boxShadow: `4px 4px 0 ${INK}` }}>6</div>
  </div>
);

const Streak7: React.FC = () => (
  <div className="relative flex items-end gap-2" data-testid="cm-streak_7">
    {[40, 46, 52, 58, 64, 70].map((h, i) => (
      <div key={h} className="cm-day cm-ledge" style={{ width: 26, height: h, background: TEAL, animationDelay: `${0.1 + i * 0.15}s`, borderRadius: 10 }} />
    ))}
    <div className="relative">
      <div className="cm-day cm-ledge" style={{ width: 34, height: 82, background: GOLD, animationDelay: '1s', borderRadius: 10 }} />
      <div className="cm-flame-pop absolute" style={{ top: -46, left: -6 }}>
        <div className="cm-flame-wiggle">
          <svg width="46" height="52" viewBox="0 0 46 52">
            <path d="M23 2 C30 14 40 18 40 32 A17 17 0 0 1 6 32 C6 22 14 18 15 8 C18 14 21 15 23 2 Z" fill={GOLD} stroke={INK} strokeWidth="3" />
            <path d="M23 24 C26 30 31 31 31 38 A8 8 0 0 1 15 38 C15 33 20 30 23 24 Z" fill={PLUM} stroke={INK} strokeWidth="2.5" />
          </svg>
        </div>
      </div>
    </div>
  </div>
);

const Breakthrough: React.FC<{ delta?: string }> = ({ delta }) => (
  <div className="flex items-end gap-3" style={{ height: 130 }} data-testid="cm-breakthrough">
    <div className="cm-ledge" style={{ width: 44, height: 60, background: '#D8CFC0' }} />
    <div className="cm-ledge" style={{ width: 44, height: 74, background: '#D8CFC0' }} />
    <div className="cm-rise cm-ledge relative" style={{ width: 52, height: 118, background: PLUM }}>
      <div className="cm-pop absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg px-2 py-1 text-xs font-extrabold text-white"
        style={{ top: -40, background: PLUM, border: `2px solid ${INK}`, boxShadow: `3px 3px 0 ${INK}`, animationDelay: '1s' }}>
        {delta ?? 'breakthrough'}
      </div>
    </div>
  </div>
);

const ExamReady: React.FC = () => (
  <div className="relative flex items-center justify-center" style={{ width: 180, height: 180 }} data-testid="cm-exam_ready">
    <svg width="180" height="180" viewBox="0 0 180 180" className="absolute">
      <g className="cm-burst" stroke={GOLD} strokeWidth="5" strokeLinecap="round">
        <line x1="90" y1="8" x2="90" y2="30" /><line x1="90" y1="150" x2="90" y2="172" />
        <line x1="8" y1="90" x2="30" y2="90" /><line x1="150" y1="90" x2="172" y2="90" />
        <line x1="32" y1="32" x2="47" y2="47" /><line x1="133" y1="133" x2="148" y2="148" />
        <line x1="148" y1="32" x2="133" y2="47" /><line x1="47" y1="133" x2="32" y2="148" />
      </g>
    </svg>
    <div className="cm-stamp flex items-center justify-center" style={{ width: 104, height: 104, borderRadius: 26, background: TEAL, border: `3px solid ${INK}`, boxShadow: `5px 5px 0 ${INK}` }}>
      <svg width="56" height="56" viewBox="0 0 56 56"><path d="M28 6 L34 20 L50 21 L38 31 L42 47 L28 38 L14 47 L18 31 L6 21 L22 20 Z" fill={GOLD} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" /></svg>
    </div>
  </div>
);

const Passed: React.FC = () => (
  <div className="cm-stamp cm-ledge inline-block px-8 py-5 text-center" data-testid="cm-passed">
    <div className="text-3xl">🎓</div>
    <div className="font-display font-bold text-3xl" style={{ color: INK }}>YOU PASSED</div>
    <div className="text-sm font-extrabold" style={{ color: '#6B6257' }}>earned, not lucky</div>
  </div>
);

const MOMENTS: Record<string, React.FC<{ bandAngle?: number; delta?: string }>> = {
  first_correct: FirstCorrect,
  diagnostic_done: DiagnosticDone,
  signup_welcome: SignupWelcome,
  first_quiz: FirstQuiz,
  streak_7: Streak7,
  breakthrough: Breakthrough,
  exam_ready: ExamReady,
  passed: Passed,
};

export interface CelebrationMomentProps {
  kind: string;
  /** diagnostic_done: needle angle for the readiness band (-90..90). */
  bandAngle?: number;
  /** breakthrough: real delta chip text, e.g. "+18 pts". */
  delta?: string;
}

/** Renders the approved animation for a catalog kind; null for unknown kinds
 *  (callers keep confetti as the fallback — new backend moments degrade
 *  gracefully until this map learns them). */
export const CelebrationMoment: React.FC<CelebrationMomentProps> = ({ kind, bandAngle, delta }) => {
  const Moment = MOMENTS[kind];
  if (!Moment) return null;
  return <Moment bandAngle={bandAngle} delta={delta} />;
};

export const hasCelebrationMoment = (kind: string): boolean => kind in MOMENTS;
